import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const isIrish = i18n.language === 'ga';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Link>
          </Button>
        </div>

        <div className="prose prose-sm dark:prose-invert max-w-none">
          {isIrish ? <IrishPolicy /> : <EnglishPolicy />}
        </div>

        <div className="mt-8 pt-6 border-t text-sm text-muted-foreground">
          <p>{t('gdpr.privacy.lastUpdated')}: May 2026</p>
        </div>
      </div>
    </div>
  );
}

function EnglishPolicy() {
  return (
    <>
      <h1 className="text-2xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-muted-foreground mb-6">Coláiste Lurgan — Indreabhán, Co. na Gaillimhe</p>

      <h2 className="text-lg font-semibold mt-6 mb-3">Definitions</h2>
      <dl className="space-y-3 text-sm">
        <div><dt className="font-medium inline">"Coláiste Lurgan", "we", "us" </dt><dd className="inline">— Coláiste Lurgan, with a registered address at Indreabhán, Co. na Gaillimhe, Ireland.</dd></div>
        <div><dt className="font-medium inline">"Personal Data" </dt><dd className="inline">— any information relating to an identified or identifiable individual.</dd></div>
        <div><dt className="font-medium inline">"Processing" </dt><dd className="inline">— any operation performed on personal data, including collection, storage, use, or deletion.</dd></div>
        <div><dt className="font-medium inline">"Data Controller" </dt><dd className="inline">— the person or organisation that decides how and why personal data is used.</dd></div>
        <div><dt className="font-medium inline">"Data Processor" </dt><dd className="inline">— a person or organisation that processes personal data on behalf of a Data Controller.</dd></div>
        <div><dt className="font-medium inline">"Sub-processor" </dt><dd className="inline">— any third party engaged by us to help deliver our services.</dd></div>
        <div><dt className="font-medium inline">"Applicable Data Protection Law" </dt><dd className="inline">— all applicable data protection laws, including the General Data Protection Regulation (GDPR) and the Data Protection Act 2018.</dd></div>
      </dl>

      <h2 className="text-lg font-semibold mt-6 mb-2">1. Introduction</h2>
      <p>
        Coláiste Lurgan ('we' and 'us') respects your privacy and processes personal data in
        accordance with the General Data Protection Regulation (GDPR) and the Data Protection
        Act 2018.
      </p>
      <p className="mt-2">
        Please read the following notice carefully to understand our views and practices in
        relation to your personal data and how we will treat it.
      </p>
      <p className="mt-2">
        Coláiste Lurgan acts as a Data Controller in relation to the personal data of students,
        parents, and guardians collected through this management system.
      </p>
      <p className="mt-3">
        <strong>Data Controller:</strong> Coláiste Lurgan<br />
        <strong>Address:</strong> Indreabhán, Co. na Gaillimhe, Ireland<br />
        <strong>Email:</strong>{' '}
        <a href="mailto:eolas@lurgan.biz" className="underline">eolas@lurgan.biz</a><br />
        <strong>Phone:</strong> +353 (91) 593182<br />
        <strong>Data Protection Contact:</strong> Colm O'Fatharta —{' '}
        <a href="mailto:eolas@lurgan.biz" className="underline">eolas@lurgan.biz</a>
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">2. Data We Collect</h2>
      <p>We collect and process personal data that is necessary to provide our services. This includes:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Student personal information (name, address, date of birth, gender)</li>
        <li>Parent and guardian contact information (name, email, phone number)</li>
        <li>School and educational information (school name, year, county)</li>
        <li>Course enrolment and registration information</li>
        <li>Payment and financial records</li>
        <li>House and accommodation assignments</li>
        <li>Account login details</li>
        <li>Security logs recording who accessed the system and when</li>
        <li>Interview notes and scores (visible to authorised staff only)</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-2">3. How We Use Data</h2>
      <p>
        We use personal data to register students for courses, manage accommodation, communicate
        with parents and guardians, process payments, and comply with our legal obligations.
      </p>
      <p className="mt-2">
        We also use data to maintain the security and integrity of the system, including
        monitoring for unauthorised access.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">4. Legal Basis</h2>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>
          <strong>Contract fulfilment:</strong> To register students for courses, manage
          accommodation, and deliver the educational programme.
        </li>
        <li>
          <strong>Legitimate interest:</strong> To maintain security, prevent fraud, and
          protect the safety of students and staff.
        </li>
        <li>
          <strong>Legal obligation:</strong> To comply with Irish tax law (financial records)
          and child safeguarding requirements.
        </li>
        <li>
          <strong>Consent:</strong> For optional preferences such as language choice and
          saved form settings.
        </li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-2">5. Data Retention</h2>
      <p>We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, including compliance with legal and regulatory obligations.</p>
      <table className="w-full text-sm border-collapse mt-3">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 pr-4">Data type</th>
            <th className="text-left py-2">Retention period</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          <tr><td className="py-2 pr-4">Active student records</td><td className="py-2">Duration of enrolment + 1 year</td></tr>
          <tr><td className="py-2 pr-4">Financial records</td><td className="py-2">7 years (Irish tax law)</td></tr>
          <tr><td className="py-2 pr-4">Security access logs</td><td className="py-2">1 year</td></tr>
          <tr><td className="py-2 pr-4">Email communication logs</td><td className="py-2">2 years</td></tr>
          <tr><td className="py-2 pr-4">Consent records</td><td className="py-2">3 years</td></tr>
        </tbody>
      </table>

      <h2 className="text-lg font-semibold mt-6 mb-2">6. Sub-processors (Third Parties)</h2>
      <p>
        We engage trusted third-party providers to assist in delivering our services. We ensure
        that all such providers meet appropriate data protection standards.
      </p>
      <p className="mt-2">
        Some of these providers may process personal data outside the European Economic Area,
        in which case appropriate safeguards are applied.
      </p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li><strong>Racket Software</strong> — software provider that built and operates this management system on behalf of Coláiste Lurgan under a Data Processing Agreement</li>
        <li><strong>Resend</strong> — email delivery service (sends communications to parents and students)</li>
        <li><strong>Hostworld</strong> — hosting infrastructure (servers located in the UK; UK has an EU adequacy decision in place)</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-2">7. Cookie Policy</h2>
      <p>
        Cookies are small files stored on your device. We use cookies and browser storage
        to keep you logged in, protect the security of the system, and — with your consent —
        to remember your preferences.
      </p>
      <table className="w-full text-sm border-collapse mt-3">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 pr-4">Purpose</th>
            <th className="text-left py-2 pr-4">What it stores</th>
            <th className="text-left py-2">Required?</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          <tr><td className="py-2 pr-4">Keeps you logged in</td><td className="py-2 pr-4 text-muted-foreground text-xs">Authentication token</td><td className="py-2">Essential</td></tr>
          <tr><td className="py-2 pr-4">Protects against security attacks</td><td className="py-2 pr-4 text-muted-foreground text-xs">Security token (csrftoken)</td><td className="py-2">Essential</td></tr>
          <tr><td className="py-2 pr-4">Remembers your cookie choices</td><td className="py-2 pr-4 text-muted-foreground text-xs">Your accept/reject preference</td><td className="py-2">Essential</td></tr>
          <tr><td className="py-2 pr-4">Remembers your language preference</td><td className="py-2 pr-4 text-muted-foreground text-xs">English or Irish (GA/EN)</td><td className="py-2">Optional</td></tr>
          <tr><td className="py-2 pr-4">Staff workflow preferences <span className="text-muted-foreground">(authorised staff only)</span></td><td className="py-2 pr-4 text-muted-foreground text-xs">Column mappings and export field selections</td><td className="py-2">Optional</td></tr>
        </tbody>
      </table>
      <p className="mt-3">
        We do not use analytics cookies, advertising cookies, or any third-party tracking.
        You can change your cookie preferences at any time using the link in the page footer.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">8. Your Rights</h2>
      <p>Under GDPR you have the right to:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
        <li><strong>Rectification</strong> — ask us to correct inaccurate data</li>
        <li><strong>Erasure</strong> — ask us to delete your data ("right to be forgotten"), subject to legal obligations</li>
        <li><strong>Restriction</strong> — ask us to limit how we use your data</li>
        <li><strong>Portability</strong> — receive your data in a structured, machine-readable format</li>
        <li><strong>Object</strong> — object to processing based on legitimate interest</li>
        <li><strong>Withdraw consent</strong> — withdraw consent for optional cookies at any time</li>
      </ul>
      <p className="mt-3">
        To exercise any of these rights, please contact us at{' '}
        <a href="mailto:eolas@lurgan.biz" className="underline">eolas@lurgan.biz</a> or by post at
        Indreabhán, Co. na Gaillimhe, Ireland.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">9. Data Security</h2>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Sensitive personal data is encrypted when stored</li>
        <li>Access is restricted by role — staff can only see data relevant to their function</li>
        <li>All data access is recorded in a security log</li>
        <li>Two-factor authentication is required for staff accounts</li>
        <li>The system is hosted on servers within the UK (EU adequacy decision applies)</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-2">10. Complaints</h2>
      <p>
        You have the right to make a complaint at any time to the Data Protection Commission
        (DPC). The DPC website, which contains the processes for raising a complaint, can be
        found at{' '}
        <a
          href="https://www.dataprotection.ie"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          www.dataprotection.ie
        </a>.
      </p>
      <p className="mt-2">
        We would ask that where possible you contact us at{' '}
        <a href="mailto:eolas@lurgan.biz" className="underline">eolas@lurgan.biz</a>{' '}
        in the first instance so we can attempt to resolve any issues you may have.
      </p>

      <div className="mt-8 pt-4 border-t text-sm text-muted-foreground space-y-2">
        <p>
          Unless otherwise specified, all references within this document to the European Union
          include all current member states of the European Union and the European Economic Area.
        </p>
        <p>This policy relates solely to this management system.</p>
        <p>
          This policy shall be governed by and construed in accordance with the laws of Ireland.
          The parties agree that the courts of Ireland shall have exclusive jurisdiction to resolve
          any disputes arising out of or in connection with this policy.
        </p>
      </div>
    </>
  );
}

function IrishPolicy() {
  return (
    <>
      <h1 className="text-2xl font-bold mb-2">Polasaí Príobháideachais</h1>
      <p className="text-muted-foreground mb-6">Coláiste Lurgan — Indreabhán, Co. na Gaillimhe</p>

      <p className="italic text-muted-foreground">
        {/* TODO: Professional Irish translation required before public launch */}
        Tá an polasaí seo ar fáil i mBéarla faoi láthair. Beidh leagan Gaeilge ar fáil go luath.
      </p>
      <p className="mt-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/privacy-policy" onClick={() => {}}>
            Read in English
          </Link>
        </Button>
      </p>

      {/* Full Irish content will be added here after professional translation */}
    </>
  );
}
